import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { Button, Empty, Modal, Typography } from 'antd';
import {
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';

import CondensedTable from 'components/CondensedTable/CondensedTable';
import useModalControls from 'hooks/useModalControls';
import { OTB_ELIGIBLITY } from 'queries/otb';
import { formatDate } from 'utilities/datesAndTimes';
import { formatDollarsFromCents } from 'utilities/helpers';
import QueryBoundary from 'components/QueryBoundary/QueryBoundary';

const pendingPaymentColumns = [
  {
    title: 'Held Date',
    dataIndex: 'date',
    key: 'date',
    render: formatDate,
  },
  {
    title: 'Merchant Channel',
    dataIndex: 'merchantChannel',
    key: 'merchantChannel',
  },
  {
    title: 'Amount',
    dataIndex: 'amount',
    key: 'amount',
    render: formatDollarsFromCents,
  },
];

const OTBEligibilityControl = ({
  transactions = [],
  onEligibilityChange = () => {},
}) => {
  const { accountId, customerId } = useParams();
  const otbReleaseEligibilityModal = useModalControls();

  const otbReleaseEligibilityQuery = useQuery(OTB_ELIGIBLITY, {
    variables: {
      accountId,
      customerId,
    },
  });

  const eligible =
    otbReleaseEligibilityQuery?.data?.account?.checkEligibility?.eligibility
      ?.eligible || false;

  const paymentOffsets = useMemo(() => {
    return transactions.filter((transaction) => {
      return transaction.isPendingAuthorization;
    });
  }, [transactions]);

  useEffect(() => {
    onEligibilityChange(eligible);
  }, [eligible]);

  return (
    <>
      <Modal
        {...otbReleaseEligibilityModal}
        centered
        cancelButtonProps={{ hidden: true }}
        title="OTB Release Eligibility"
        width={800}
        styles={{ body: { paddingTop: 'var(--spacing-xs)' } }}
      >
        <QueryBoundary
          query={otbReleaseEligibilityQuery}
          loadingMessage="Loading OTB release eligibility"
          errorMessage="Failed to retrieve OTB release eligibility"
        >
          <Typography.Paragraph strong>
            Eligibility:{' '}
            <Typography.Text type={eligible ? 'success' : 'warning'}>
              {eligible ? 'Eligible' : 'Ineligible'}
            </Typography.Text>
          </Typography.Paragraph>

          <Typography.Paragraph strong>Held Payments</Typography.Paragraph>
          <CondensedTable
            columns={pendingPaymentColumns}
            data={paymentOffsets}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No held payments"
                />
              ),
            }}
          />
        </QueryBoundary>
      </Modal>
      <Button
        data-testid="otb-release-eligibility-button"
        onClick={otbReleaseEligibilityModal.show}
        loading={otbReleaseEligibilityQuery.loading}
        icon={
          otbReleaseEligibilityQuery.error ? (
            <CloseCircleOutlined style={{ color: 'var(--red-3)' }} />
          ) : eligible ? (
            <CheckCircleOutlined style={{ color: 'var(--green-3)' }} />
          ) : (
            <WarningOutlined style={{ color: 'var(--yellow-3)' }} />
          )
        }
      >
        OTB Release Eligibility
      </Button>
    </>
  );
};

export default OTBEligibilityControl;
